import { normalizePersistedImageUrl, uploadsPathFromSignedViewUrl } from '@/utils/imageUrlUtils';
import { imageStorageAPI } from '@/utils/api';
import { MODEL_ETHNICITY_PRESET_ASSET_BASE } from '@/pages/ipe/workbench/constants/modelEthnicityPresetConfig';
import {
  MODEL_TRY_ON_PRESET_ASSET_BASE,
  resolveTryOnPresetSubmitUrl,
} from '@/pages/ipe/workbench/constants/modelTryOnPresetConfig';

function stripLeadingSlashBeforeHttp(url) {
  return String(url || '').trim().replace(/^\/+(https?:\/\/)/i, '$1');
}

/** 系统模特静态图由后端本地读取，勿转成 localhost 绝对地址（后端/DashScope 无法拉取） */
function isLocalSystemModelPresetAssetPath(url) {
  const trimmed = String(url || '').trim();
  return trimmed.startsWith(`${MODEL_TRY_ON_PRESET_ASSET_BASE}/`)
    || trimmed.startsWith(`${MODEL_ETHNICITY_PRESET_ASSET_BASE}/`);
}

/** 导航 hub / 工作台空态案例图（public/images/ipe/...） */
function isNavHubStaticAssetPath(url) {
  const trimmed = String(url || '').trim();
  return trimmed.startsWith('/images/ipe/');
}

function isLocalOrSameOriginHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;
  if (typeof window === 'undefined') return false;
  try {
    return host === window.location.hostname;
  } catch {
    return false;
  }
}

/**
 * 把本机/同源的绝对 uploads 或签名读图地址收敛为 /uploads/...，
 * 避免提交后后端 SSRF 拦截或 DashScope 无法访问 localhost。
 */
function toLocalUploadsRelativePath(raw) {
  const trimmed = stripLeadingSlashBeforeHttp(raw);
  if (!trimmed) return '';

  const fromSignedView = uploadsPathFromSignedViewUrl(trimmed);
  if (fromSignedView) return fromSignedView;

  if (trimmed.startsWith('/uploads/')) return trimmed;
  if (trimmed.startsWith('uploads/')) return `/${trimmed}`;

  if (!(trimmed.startsWith('http://') || trimmed.startsWith('https://'))) {
    return '';
  }

  try {
    const parsed = new URL(trimmed);
    if (!isLocalOrSameOriginHost(parsed.hostname)) return '';
    if (parsed.pathname.startsWith('/uploads/')) {
      return parsed.pathname;
    }
    const nested = uploadsPathFromSignedViewUrl(parsed.pathname + parsed.search);
    return nested || '';
  } catch {
    return '';
  }
}

function normalizeImg2ImgApiUrl(raw) {
  const trimmed = stripLeadingSlashBeforeHttp(raw);
  if (!trimmed) return '';

  const localUploads = toLocalUploadsRelativePath(trimmed);
  if (localUploads) return localUploads;

  const persisted = normalizePersistedImageUrl(trimmed);
  if (persisted) return persisted;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('uploads/')) {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }

  return trimmed;
}

async function persistFetchedImageBlob(blob, folder = 'image-space') {
  if (!blob || blob.size <= 0) return '';
  try {
    const ext = (blob.type || '').includes('png') ? 'png' : (blob.type || '').includes('webp') ? 'webp' : 'jpg';
    const file = new File([blob], `ref-${Date.now()}.${ext}`, {
      type: blob.type || 'image/jpeg',
    });
    const saved = await imageStorageAPI.save(file, { folder });
    const path = saved?.url || saved?.imageUrl || '';
    return normalizeImg2ImgApiUrl(path);
  } catch {
    return '';
  }
}

async function persistPublicStaticAsset(raw, folder = 'image-space') {
  const trimmed = String(raw || '').trim();
  if (!trimmed || typeof window === 'undefined') return '';
  const absolute = trimmed.startsWith('http://') || trimmed.startsWith('https://')
    ? trimmed
    : `${window.location.origin}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
  try {
    const response = await fetch(absolute);
    if (!response.ok) return '';
    const blob = await response.blob();
    return persistFetchedImageBlob(blob, folder);
  } catch {
    return '';
  }
}

async function persistBlobOrDataUrl(raw, folder = 'image-space') {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  try {
    let input = trimmed;
    if (trimmed.startsWith('blob:')) {
      const response = await fetch(trimmed);
      if (!response.ok) return '';
      const blob = await response.blob();
      return persistFetchedImageBlob(blob, folder);
    }
    const saved = await imageStorageAPI.save(input, { folder });
    const path = saved?.url || saved?.imageUrl || '';
    return normalizeImg2ImgApiUrl(path);
  } catch {
    return '';
  }
}

/**
 * 提交万能改图 / AI 文案 API 用的参考图地址：优先 /uploads/ 相对路径（IPE 内置引擎可直接读盘），
 * OSS 则保留完整 https；blob/data 会先落盘再返回持久地址。
 */
export async function resolveImg2ImgReferenceForApi(rawUrl) {
  const trimmed = stripLeadingSlashBeforeHttp(rawUrl);
  if (!trimmed) return '';

  const localUploads = toLocalUploadsRelativePath(trimmed);
  if (localUploads) return localUploads;

  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('uploads/')) {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }

  // 系统模特静态图由后端本地读取，扩展名须与磁盘文件一致（展示 webp 回退不在此改写）
  if (isLocalSystemModelPresetAssetPath(trimmed)) {
    return resolveTryOnPresetSubmitUrl(trimmed);
  }

  if (isNavHubStaticAssetPath(trimmed)) {
    return persistPublicStaticAsset(trimmed);
  }

  if (trimmed.startsWith('/assets/') && typeof window !== 'undefined') {
    return `${window.location.origin}${trimmed}`;
  }

  if (trimmed.startsWith('data:image/') || trimmed.startsWith('blob:')) {
    return persistBlobOrDataUrl(trimmed);
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  return normalizeImg2ImgApiUrl(trimmed);
}

/** 判断是否已是后端可直接消费的持久图片地址（非临时 blob/data） */
export function isPersistableImg2ImgApiUrl(url) {
  const trimmed = stripLeadingSlashBeforeHttp(url);
  if (!trimmed) return false;
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) return false;
  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('uploads/')) return true;
  if (isLocalSystemModelPresetAssetPath(trimmed)) return true;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return true;
  return Boolean(normalizePersistedImageUrl(trimmed) || toLocalUploadsRelativePath(trimmed));
}
